import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking"); // checking | paid | failed

  useEffect(() => {
    if (!sessionId) {
      setStatus("failed");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      attempts += 1;
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (["failed", "expired"].includes(data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (e) {
        /* keep polling */
      }
      if (attempts >= 8) {
        setStatus("pending");
        return;
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="grain flex min-h-screen items-center justify-center bg-[#09090B] px-5">
      <div className="w-full max-w-md border border-zinc-800 bg-surface-100 p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-blaze" />
            <h1 className="mt-6 font-display text-4xl uppercase tracking-tight">
              Confirming…
            </h1>
            <p className="mt-3 font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
              Verifying your payment
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-400" />
            <h1 className="mt-6 font-display text-5xl uppercase leading-none tracking-tight">
              You're in
              <br />
              <span className="text-blaze">God Mode.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-zinc-400">
              Order confirmed. Your OVERCLOCK is on its way — check your inbox
              for the receipt and tracking. Time to overclock.
            </p>
            <Link
              to="/"
              data-testid="back-home-btn"
              className="mt-8 inline-flex items-center gap-2 bg-blaze px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.12em] text-black transition-transform duration-200 hover:-translate-y-1"
            >
              <Zap className="h-4 w-4" fill="currentColor" /> Back to site
            </Link>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 className="mx-auto h-12 w-12 animate-spin text-blaze" />
            <h1 className="mt-6 font-display text-3xl uppercase tracking-tight">
              Almost there
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              Your payment is still processing. You'll get an email as soon as
              it's confirmed.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 border border-zinc-700 px-6 py-3 font-mono text-xs uppercase tracking-[0.15em] text-zinc-300 hover:border-white hover:text-white"
            >
              Back to site
            </Link>
          </>
        )}
        {status === "failed" && (
          <>
            <XCircle className="mx-auto h-14 w-14 text-red-500" />
            <h1 className="mt-6 font-display text-4xl uppercase tracking-tight">
              Payment issue
            </h1>
            <p className="mt-3 text-sm text-zinc-400">
              We couldn't confirm your payment. No charge was made — please try
              again.
            </p>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 bg-blaze px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.12em] text-black"
            >
              Try again
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
